import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as VehicleDown } from '../../assets/img/mobile-car-map-down.svg';
import { ReactComponent as VehicleUp } from '../../assets/img/mobile-car-map-up.svg';

function VehicleConditionMap({
  damages,
  onMapClick,
  onMarkerClick,
  className = '',
  mapAriaLabel = 'Vehicle condition map - click to add damage markers',
  markerAriaLabelPrefix = 'Damage marker',
  enableKeyboardNavigation = true,
  showDamageTooltips = true,
  maxDamageMarkers = 50,
  coordinateValidation = true,
  context = '',
}) {
  const vehicleDownRef = useRef(null);
  const vehicleUpRef = useRef(null);
  const [imageDimensions, setImageDimensions] = useState({
    bottom: { width: 0, height: 0 },
    top: { width: 0, height: 0 },
  });
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      // Use a requestAnimationFrame to avoid layout thrashing
      window.requestAnimationFrame(() => {
        const newDimensions = { bottom: {}, top: {} };
        // eslint-disable-next-line no-restricted-syntax
        for (const entry of entries) {
          const section = entry.target.parentElement.getAttribute('data-section');
          if (section === 'bottom' || section === 'top') {
            newDimensions[section] = {
              width: entry.contentRect.width,
              height: entry.contentRect.height,
            };
          }
        }
        setImageDimensions((prev) => ({ ...prev, ...newDimensions }));
      });
    });

    if (vehicleDownRef.current) observer.observe(vehicleDownRef.current);
    if (vehicleUpRef.current) observer.observe(vehicleUpRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const validateCoordinates = useCallback(
    (x, y, section) => {
      if (!coordinateValidation) return true;

      const dimensions = imageDimensions[section];
      if (!dimensions.width || !dimensions.height) return false;

      return x >= 0 && x <= dimensions.width && y >= 0 && y <= dimensions.height;
    },
    [coordinateValidation, imageDimensions],
  );

  const handleBottomMapClick = useCallback(
    (event) => {
      const rect = vehicleDownRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      if (!validateCoordinates(x, y, 'bottom')) {
        console.warn('Invalid coordinates for damage marker:', { x, y, section: 'bottom' });
        return;
      }

      if (damages.length >= maxDamageMarkers) {
        console.warn(`Maximum number of damage markers (${maxDamageMarkers}) reached`);
        return;
      }

      // Convert to relative coordinates (0-1 range)
      const relativeX = x / rect.width;
      const relativeY = y / rect.height;

      onMapClick(relativeX, relativeY, 'bottom');
    },
    [onMapClick, validateCoordinates, damages.length, maxDamageMarkers],
  );

  const handleTopMapClick = useCallback(
    (event) => {
      const rect = vehicleUpRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      if (!validateCoordinates(x, y, 'top')) {
        console.warn('Invalid coordinates for damage marker:', { x, y, section: 'top' });
        return;
      }

      if (damages.length >= maxDamageMarkers) {
        console.warn(`Maximum number of damage markers (${maxDamageMarkers}) reached`);
        return;
      }

      // Convert to relative coordinates (0-1 range)
      const relativeX = x / rect.width;
      const relativeY = y / rect.height;

      onMapClick(relativeX, relativeY, 'top');
    },
    [onMapClick, validateCoordinates, damages.length, maxDamageMarkers],
  );

  const handleKeyDown = useCallback(
    (section) => (event) => {
      if (!enableKeyboardNavigation) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        // Place marker at center of section
        onMapClick(0.5, 0.5, section);
      }
    },
    [onMapClick, enableKeyboardNavigation],
  );

  // Helper function to get marker position based on damage data
  const getMarkerPosition = useCallback(
    (damage) => {
      // 1. Determine which dimensions to use ('top' or 'bottom') based on the backend section name
      const sectionKey = damage.section === 'EXTERNAL' ? 'bottom' : 'top';
      const dimensions = imageDimensions[sectionKey];

      // 2. Guard against unmeasured dimensions (will be corrected by ResizeObserver)
      if (!dimensions || !dimensions.width || !dimensions.height) {
        return { left: 0, top: 0 };
      }

      // 3. ALWAYS use coordinateX/Y as relative values and apply them to the current dimensions.
      // This now works for both newly created and reloaded markers.
      return {
        left: damage.coordinateX * dimensions.width,
        top: damage.coordinateY * dimensions.height,
      };
    },
    [imageDimensions],
  );

  const createDamageMarkers = useCallback(
    (section) => {
      const backendSectionName = section === 'bottom' ? 'EXTERNAL' : 'INTERNAL';

      const sectionDamages = damages.filter((damage) => damage.section === backendSectionName);

      return sectionDamages.map((damage, index) => {
        const markerId = `damage-marker-${damage.id || index}-${section}`;
        const damageTypeDisplay = damage.damageType?.label || damage.damageType || 'Unknown';
        const markerLabel = `${markerAriaLabelPrefix} ${index + 1}: ${damageTypeDisplay} (${section} view)`;
        const position = getMarkerPosition(damage);
        const globalIndex = damages.findIndex((d) => d.id === damage.id);

        return (
          <div
            key={damage.id || `${index}-${section}`}
            id={markerId}
            className="damage-marker"
            style={{
              position: 'absolute',
              left: `${position.left}px`,
              top: `${position.top}px`,
              transform: 'translate(-50%, -50%)',
            }}
            role="button"
            tabIndex={0}
            aria-label={markerLabel}
            title={
              showDamageTooltips
                ? `${damageTypeDisplay}: ${damage.description || 'No description'}`
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              if (onMarkerClick) {
                onMarkerClick(context === 'details' ? globalIndex : damage.id);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                if (onMarkerClick) {
                  onMarkerClick(context === 'details' ? globalIndex : damage.id);
                }
              }
            }}
          >
            <div className="damage-marker-circle" aria-hidden="true">
              {globalIndex + 1}
            </div>
          </div>
        );
      });
    },
    [
      damages,
      markerAriaLabelPrefix,
      getMarkerPosition,
      showDamageTooltips,
      onMarkerClick,
      context,
    ],
  );

  const mapInstructions = useMemo(() => {
    const remainingSlots = Math.max(0, maxDamageMarkers - damages.length);
    return `${mapAriaLabel}. Currently ${damages.length} damage markers placed. ${remainingSlots} slots remaining.`;
  }, [mapAriaLabel, damages.length, maxDamageMarkers]);

  return (
    <section className={`vehicle-condition-map ${className}`.trim()}>
      <div className="vehicle-maps-container">
        <div className="vehicle-map-section" data-section="bottom">
          <div
            ref={vehicleDownRef}
            className="vehicle-map-image-container"
            onClick={handleBottomMapClick}
            onKeyDown={handleKeyDown('bottom')}
            role="button"
            tabIndex={0}
            aria-label={`Bottom view - ${mapInstructions}`}
            aria-describedby="map-instructions"
            style={{ position: 'relative', cursor: 'pointer', width: '100%' }}
          >
            <VehicleDown aria-hidden="true" />
            <div className="damage-markers-container">{createDamageMarkers('bottom')}</div>
          </div>
        </div>

        <div className="vehicle-map-section" data-section="top">
          <div
            ref={vehicleUpRef}
            className="vehicle-map-image-container"
            onClick={handleTopMapClick}
            onKeyDown={handleKeyDown('top')}
            role="button"
            tabIndex={0}
            aria-label={`Top view - ${mapInstructions}`}
            aria-describedby="map-instructions"
            style={{ position: 'relative', cursor: 'pointer', width: '100%' }}
          >
            <VehicleUp aria-hidden="true" />
            <div className="damage-markers-container">{createDamageMarkers('top')}</div>
          </div>
        </div>
      </div>

      <div id="map-instructions" className="visually-hidden">
        Click on the vehicle diagrams to add damage markers. Use Tab to navigate between
        markers, Enter or Space to activate. {damages.length} of {maxDamageMarkers} damage
        markers placed.
      </div>
    </section>
  );
}

VehicleConditionMap.propTypes = {
  damages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      // Support both legacy absolute coordinates and new relative coordinates
      x: PropTypes.number, // Legacy support
      y: PropTypes.number, // Legacy support
      relativeX: PropTypes.number, // New relative coordinate (0-1)
      relativeY: PropTypes.number, // New relative coordinate (0-1)
      section: PropTypes.oneOf(['top', 'bottom']), // Vehicle section
      damageType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string,
        }),
      ]),
      description: PropTypes.string,
    }),
  ).isRequired,
  onMapClick: PropTypes.func.isRequired,
  onMarkerClick: PropTypes.func,
  className: PropTypes.string,
  mapAriaLabel: PropTypes.string,
  markerAriaLabelPrefix: PropTypes.string,
  enableKeyboardNavigation: PropTypes.bool,
  showDamageTooltips: PropTypes.bool,
  maxDamageMarkers: PropTypes.number,
  coordinateValidation: PropTypes.bool,
  context: PropTypes.oneOf(['details', 'addVehicle']),
};

VehicleConditionMap.defaultProps = {
  className: '',
  mapAriaLabel: 'Vehicle condition map - click to add damage markers',
  markerAriaLabelPrefix: 'Damage marker',
  enableKeyboardNavigation: true,
  showDamageTooltips: true,
  maxDamageMarkers: 50,
  coordinateValidation: true,
  onMarkerClick: undefined,
  context: 'addVehicle',
};

export default VehicleConditionMap;
